import {
  ExtrudeGeometry,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  Shape,
  Vector2,
  Vector2Like,
  Vector3,
  Vector3Like,
} from 'three'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { DenormalizedWall, WallSegment } from '~/store/objects/types'
import { modelToUI, vectorModelToUI, vectorUIToModel } from '../util/units'
import floorFrag from './floor.frag?raw'
import floorVert from './floor.vert?raw'
import { Html, shaderMaterial } from '@react-three/drei'
import { extend, ShaderMaterialProps } from '@react-three/fiber'
import { selectObjects } from '~/store/selectedObjects'
import ObjectClassName from '~/config/objectClassNames'
import theme from '~/config/theme'
import { useDistanceDimensions } from '~/hooks/useDistanceDimensions'
import { R3FDimensionInput } from '~/ui/R3FDimensionInput'
import { facilityIsRectangle } from '~/store/objects/selectors'
import { updateObjects } from '~/store/objects'
import { forwardRef, MutableRefObject, useImperativeHandle, useRef, useState } from 'react'
import { DragControls } from '../util/DragControls'
import LayerKeys from '~/config/layerKeys'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'
import { getRenderOrder } from '~/components/DrawingCanvas/util/renderOrder'
import { inputStyles } from '~/ui/Field'
import { selectDenormalizedWalls, selectExteriorWallMinHeight } from '~/store/objects/selectors/walls'
import { getSegmentOffsets, getSegmentShape } from '~/components/DrawingCanvas/Walls/util'
import { denormalizeWalls } from '~/components/DrawingCanvas/lib/collisionMeshes'

type StartDragEventData = {
  segment: string
}

type DragEventData = {
  delta: Vector3Like
}

declare global {
  interface WindowEventMap {
    'Walls/startDrag': CustomEvent<StartDragEventData>
    'Walls/drag': CustomEvent<DragEventData>
    'Walls/stopDrag': CustomEvent<undefined>
  }
}

type FloorMaterialProps = {
  resolution?: Vector2
  thickness?: number
  selected?: boolean
  opacity?: number
}

const FloorMaterial = shaderMaterial(
  {
    resolution: new Vector2(1, 1),
    thickness: 1.5,
    selected: false,
  },
  floorVert,
  floorFrag
)

extend({ FloorMaterial })

declare module '@react-three/fiber' {
  interface ThreeElements {
    floorMaterial: ShaderMaterialProps & FloorMaterialProps
  }
}

export function Walls() {
  const walls = useAppSelector(selectDenormalizedWalls)

  return (
    <>
      {walls.map(wall => (
        <Wall wall={wall} key={wall.id} />
      ))}
    </>
  )
}

type DragRef = {
  start(index: number): void
  drag(position: Vector3Like): void
  stop(): void
}

type DragObjects = {
  root: Object3D
  segmentsMeshes: Mesh[]
  index: number
  updatedSegments: WallSegment[] | null
}

type LabelPosition = { key: string; position: Vector3Like; distance: number }

type DragLabelsRef = {
  set(positions: LabelPosition[]): void
}

const DragWallLabels = forwardRef<DragLabelsRef>(function DragWallLabels(_props, ref) {
  const [labels, setLabels] = useState<{ key: string; position: Vector3Like; distance: number }[]>([])
  const { displayFormattedValue } = useDistanceDimensions()

  useImperativeHandle(ref, () => ({ set: positions => setLabels(positions) }), [])

  return (
    <>
      {labels.map(it => (
        <Html
          center
          key={it.key}
          position={[modelToUI(it.position.x), modelToUI(it.position.y), modelToUI(it.position.z)]}
          className={inputStyles({ isOnCanvas: true, class: 'pointer-events-none' })}
        >
          {displayFormattedValue(it.distance)}
        </Html>
      ))}
    </>
  )
})

const convertEdgeModelToUI = (edge: { start: Vector2Like; end: Vector2Like }) => ({
  start: vectorModelToUI(edge.start),
  end: vectorModelToUI(edge.end),
})

function Wall({ wall }: { wall: ReturnType<typeof denormalizeWalls>[number] }) {
  const dispatch = useAppDispatch()
  const visible = useAppSelector(state => state.layers.layers[wall.layerKey].visible)
  const interiorWallFullHeight = useAppSelector(selectExteriorWallMinHeight)
  const [isDragging, setIsDragging] = useState(false)
  const draggingObj = useRef<DragObjects>({
    root: new Object3D(),
    segmentsMeshes: [],
    index: -1,
    updatedSegments: null,
  })
  const labels = useRef<DragLabelsRef>(null)
  const ref = useRef<DragRef>(null!)

  useImperativeHandle(
    ref,
    () => ({
      start(index) {
        setIsDragging(true)
        draggingObj.current.segmentsMeshes.forEach(segment => (segment.material as MeshLambertMaterial).dispose())
        draggingObj.current.segmentsMeshes.forEach(segment => segment.geometry.dispose())
        draggingObj.current.root.clear()

        const newSegments = wall.segments.map(({ materialIndex, height, isFullHeight, insetEdge, outsetEdge }) => {
          const material = new MeshLambertMaterial({
            color: '#888888',
            transparent: true,
            depthWrite: false,
            opacity: 1 - (materialIndex ?? 0) / 10,
          })
          const shape = getSegmentShape({
            insetEdge: convertEdgeModelToUI(insetEdge),
            outsetEdge: convertEdgeModelToUI(outsetEdge),
          })
          const segmentHeight = isFullHeight ? interiorWallFullHeight : height
          const geometry = new ExtrudeGeometry(shape, { depth: modelToUI(segmentHeight) })
          return new Mesh(geometry, material)
        })

        draggingObj.current.root.add(...newSegments)
        draggingObj.current.segmentsMeshes = newSegments
        draggingObj.current.index = index

        window.dispatchEvent(
          new CustomEvent<StartDragEventData>('Walls/startDrag', { detail: { segment: wall.segments[index].id } })
        )
      },
      drag(dragDelta) {
        if (draggingObj.current.index === -1) return

        const draggingSegment = wall.segments[draggingObj.current.index]
        const start = new Vector3().copy(draggingSegment.startPoint)
        const end = new Vector3().copy(draggingSegment.endPoint)
        const direction = new Vector3().subVectors(start, end).normalize()
        direction.set(-direction.y, direction.x, direction.z)
        const clampedDelta = new Vector3().copy(dragDelta).projectOnVector(direction)
        const delta = vectorUIToModel(clampedDelta)
        window.dispatchEvent(new CustomEvent<DragEventData>('Walls/drag', { detail: { delta: clampedDelta } }))

        const updatedSegments: WallSegment[] = wall.segments.map((segment, i, { length }) => {
          const isCurrentDraggingSegment = i === draggingObj.current.index
          const isSegmentPriorToDraggingSegment = i === mod(draggingObj.current.index - 1, length)
          const isSegmentAfterDraggingSegment = i === mod(draggingObj.current.index + 1, length)
          const startPoint = new Vector3().copy(segment.startPoint)
          const endPoint = new Vector3().copy(segment.endPoint)

          if (isCurrentDraggingSegment || isSegmentPriorToDraggingSegment) endPoint.add(delta)
          if (isCurrentDraggingSegment || isSegmentAfterDraggingSegment) startPoint.add(delta)

          return { ...segment, startPoint, endPoint }
        })

        const labelEntries: LabelPosition[] = []
        const { insetEdges, outsetEdges } = getSegmentOffsets(updatedSegments)
        updatedSegments.forEach(({ height, isFullHeight, startPoint, endPoint }, i) => {
          const [insetStart, insetEnd] = insetEdges[i]
          const [outsetStart, outsetEnd] = outsetEdges[i]

          labelEntries.push({
            key: wall.segments[i].id,
            position: new Vector3().addVectors(startPoint, endPoint).divideScalar(2),
            distance: new Vector3(...insetStart, 0).distanceTo({ x: insetEnd[0], y: insetEnd[1], z: 0 }),
          })

          const shape = getSegmentShape({
            insetEdge: {
              start: new Vector2(modelToUI(insetStart[0]), modelToUI(insetStart[1])),
              end: new Vector2(modelToUI(insetEnd[0]), modelToUI(insetEnd[1])),
            },
            outsetEdge: {
              start: new Vector2(modelToUI(outsetStart[0]), modelToUI(outsetStart[1])),
              end: new Vector2(modelToUI(outsetEnd[0]), modelToUI(outsetEnd[1])),
            },
          })
          const segmentHeight = isFullHeight ? interiorWallFullHeight : height
          const geometry = new ExtrudeGeometry(shape, { depth: modelToUI(segmentHeight) })
          draggingObj.current.segmentsMeshes[i].geometry.dispose()
          draggingObj.current.segmentsMeshes[i].geometry = geometry
        })

        draggingObj.current.updatedSegments = updatedSegments
        labels.current?.set(labelEntries)
      },
      stop() {
        if (draggingObj.current.index === -1) return

        setIsDragging(false)
        window.dispatchEvent(new CustomEvent('Walls/stopDrag'))
        labels.current?.set([])
        if (draggingObj.current.updatedSegments !== null) {
          dispatch(updateObjects(draggingObj.current.updatedSegments))
        }
        draggingObj.current.updatedSegments = null
      },
    }),
    [wall, interiorWallFullHeight]
  )

  return (
    <group visible={visible}>
      <group visible={!isDragging}>
        {wall.segments.map((segment, idx) => (
          <SegmentMesh
            segment={segment}
            index={idx}
            drag={ref}
            isDragging={isDragging}
            segments={wall.segments}
            wallID={wall.id}
            key={segment.id}
          />
        ))}
        <FloorMesh wall={wall} />
      </group>
      <primitive visible={isDragging} object={draggingObj.current.root} />
      <DragWallLabels ref={labels} />
    </group>
  )
}

function mod(n: number, d: number): number {
  return ((n % d) + d) % d
}

function SegmentMesh({
  segment,
  segments,
  wallID,
  index,
  isDragging,
  drag,
}: {
  segment: ReturnType<typeof denormalizeWalls>[number]['segments'][number]
  segments: WallSegment[]
  wallID: string
  index: number
  isDragging: boolean
  drag: MutableRefObject<DragRef>
}) {
  const selectedObjects = useAppSelector(state => state.selectedObjects)
  const selected = selectedObjects.some(({ id }) => id === segment.id || id === segment.parentId)
  const wallSelected = selectedObjects.some(sel => sel.id === wallID)
  const locked = useAppSelector(state => state.layers.layers[segment.layerKey].locked)
  const isRectangular = useAppSelector(facilityIsRectangle)
  const interiorWallFullHeight = useAppSelector(selectExteriorWallMinHeight)
  const segmentHeight = segment.isFullHeight ? interiorWallFullHeight : segment.height
  const dispatch = useAppDispatch()

  const { displayFormattedValue } = useDistanceDimensions()

  const { startPoint: startPt, endPoint: endPt, insetEdge, outsetEdge } = segment
  const { start: insetStart, end: insetEnd } = insetEdge
  const isVisibleLabel = !isDragging && (wallSelected || selected)
  const shape = getSegmentShape({
    insetEdge: convertEdgeModelToUI(insetEdge),
    outsetEdge: convertEdgeModelToUI(outsetEdge),
  })
  const labelPosition = new Vector3(modelToUI(startPt.x + endPt.x) / 2, modelToUI(startPt.y + endPt.y) / 2, 0)
  const segmentLength = insetStart.distanceTo(insetEnd)

  return (
    <>
      <DragControls
        name={segment.layerKey}
        dragDisabled={locked}
        onTap={e => {
          if (e.shiftKey) {
            const newSelectedObjects = selected
              ? selectedObjects.filter(object => object.id !== segment.id)
              : [...selectedObjects, { id: segment.id, className: ObjectClassName.WALL_SEGMENT }]
            dispatch(selectObjects({ objects: newSelectedObjects }))
          } else {
            dispatch(selectObjects({ objects: [{ id: segment.id, className: ObjectClassName.WALL_SEGMENT }] }))
          }
        }}
        onDragStart={() => {
          if (!selected) return
          drag.current.start(index)
        }}
        onDrag={(_, delta) => {
          drag.current.drag(delta)
        }}
        onDragEnd={() => {
          drag.current.stop()
        }}
      >
        <mesh>
          <extrudeGeometry args={[shape, { depth: modelToUI(segmentHeight) }]} />
          <meshLambertMaterial
            color={selected ? theme.colors.three.selected : '#888888'}
            transparent
            opacity={1 - (segment.materialIndex ?? 0) / 10}
          />
        </mesh>
      </DragControls>
      {isVisibleLabel &&
        (locked ? (
          <Html
            center
            position={labelPosition}
            className={inputStyles({ isOnCanvas: true, class: 'pointer-events-none' })}
          >
            {displayFormattedValue(segmentLength)}
          </Html>
        ) : (
          <R3FDimensionInput
            center
            position={labelPosition}
            aria-label="Segment length"
            value={segmentLength}
            onCommit={newInsetLength => {
              const lengthDelta = newInsetLength - segmentLength
              const newLength = new Vector3().copy(startPt).distanceTo(endPt) + lengthDelta
              const newSegments = [...segments]

              const ourIndex = segments.findIndex(other => other.id === segment.id)
              const prevIndex = mod(ourIndex - 1, segments.length)
              const nextIndex = mod(ourIndex + 1, segments.length)

              const our = newSegments[ourIndex]

              const startToEnd = new Vector3().subVectors(our.startPoint, our.endPoint).normalize()
              const endToStart = new Vector3().subVectors(our.endPoint, our.startPoint).normalize()
              const midpoint = new Vector3().addVectors(our.startPoint, our.endPoint).divideScalar(2)

              const halfLength = newLength / 2

              const newStart = endToStart.multiplyScalar(halfLength).add(midpoint)
              const newEnd = startToEnd.multiplyScalar(halfLength).add(midpoint)

              newSegments[ourIndex] = {
                ...our,
                startPoint: { x: newEnd.x, y: newEnd.y, z: newEnd.z },
                endPoint: { x: newStart.x, y: newStart.y, z: newStart.z },
              }
              newSegments[prevIndex] = {
                ...newSegments[prevIndex],
                endPoint: { x: newEnd.x, y: newEnd.y, z: newEnd.z },
              }
              newSegments[nextIndex] = {
                ...newSegments[nextIndex],
                startPoint: { x: newStart.x, y: newStart.y, z: newStart.z },
              }

              if (isRectangular) {
                const oppositeIndex = mod(ourIndex + 2, segments.length)
                const oppositePrevIndex = mod(oppositeIndex - 1, segments.length)
                const oppositeNextIndex = mod(oppositeIndex + 1, segments.length)

                const opposite = newSegments[oppositeIndex]

                const oppositeStartToEnd = new Vector3().subVectors(opposite.startPoint, opposite.endPoint).normalize()
                const oppositeEndToStart = new Vector3().subVectors(opposite.endPoint, opposite.startPoint).normalize()
                const oppositeMidpoint = new Vector3()
                  .addVectors(opposite.startPoint, opposite.endPoint)
                  .divideScalar(2)

                const oppositeHalfLength = newLength / 2

                const oppositeNewStart = oppositeEndToStart.multiplyScalar(oppositeHalfLength).add(oppositeMidpoint)
                const oppositeNewEnd = oppositeStartToEnd.multiplyScalar(oppositeHalfLength).add(oppositeMidpoint)

                newSegments[oppositeIndex] = {
                  ...opposite,
                  startPoint: { x: oppositeNewEnd.x, y: oppositeNewEnd.y, z: oppositeNewEnd.z },
                  endPoint: { x: oppositeNewStart.x, y: oppositeNewStart.y, z: oppositeNewStart.z },
                }
                newSegments[oppositePrevIndex] = {
                  ...newSegments[oppositePrevIndex],
                  endPoint: { x: oppositeNewEnd.x, y: oppositeNewEnd.y, z: oppositeNewEnd.z },
                }
                newSegments[oppositeNextIndex] = {
                  ...newSegments[oppositeNextIndex],
                  startPoint: { x: oppositeNewStart.x, y: oppositeNewStart.y, z: oppositeNewStart.z },
                }
              }

              dispatch(updateObjects(newSegments))
            }}
          />
        ))}
    </>
  )
}

function FloorMesh({ wall }: { wall: DenormalizedWall }) {
  const selectedObjects = useAppSelector(state => state.selectedObjects)
  const selected = selectedObjects.some(sel => sel.id === wall.id)
  const dispatch = useAppDispatch()

  const points = wall.segments.map(segment => segment.startPoint)
  points.push(wall.segments[wall.segments.length - 1].endPoint)

  const shape = new Shape()
  shape.moveTo(modelToUI(points[0].x), modelToUI(points[0].y))
  for (const point of points.slice(1, points.length - 1)) {
    shape.lineTo(modelToUI(point.x), modelToUI(point.y))
  }
  const end = wall.segments[wall.segments.length - 1].endPoint
  shape.lineTo(modelToUI(end.x), modelToUI(end.y))
  shape.closePath()

  return (
    <mesh
      renderOrder={getRenderOrder(LayerKeys.EXTERIOR_WALLS)}
      name={LayerKeys.EXTERIOR_WALLS}
      onClick={handleClick(e => {
        if (e.shiftKey) {
          const newSelectedObjects = selected
            ? selectedObjects.filter(object => object.id !== wall.id)
            : [...selectedObjects, { id: wall.id, className: ObjectClassName.WALL }]
          dispatch(selectObjects({ objects: newSelectedObjects }))
        } else {
          dispatch(selectObjects({ objects: [{ id: wall.id, className: ObjectClassName.WALL }] }))
        }
      })}
    >
      <shapeGeometry args={[shape]} />
      <floorMaterial transparent depthWrite={false} selected={selected} />
    </mesh>
  )
}
